/** Settings: availability, notifications (enable + test), theme, this device, sign out. */
import { BottomSheet, ThemeToggle, useToast, formatJalali } from '@hopex/ui';
import { Bell, BellRing, LogOut, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import { forgetDevice } from '../lib/device.ts';
import { enablePush, pushState, type PushState } from '../lib/platform.ts';
import type { Me } from '../lib/types.ts';

const PUSH_FA: Record<PushState, string> = {
  granted: 'روشن',
  default: 'هنوز روشن نشده',
  denied: 'در تنظیمات گوشی خاموش است',
  unsupported: 'این مرورگر پشتیبانی نمی‌کند',
  'needs-install': 'اول برنامه را به صفحه‌ی اصلی اضافه کنید',
};

export function SettingsSheet({
  me,
  onClose,
  onMe,
  onSignedOut,
}: {
  me: Me;
  onClose: () => void;
  onMe: (m: Me) => void;
  onSignedOut: () => void;
}) {
  const toast = useToast();
  const [state, setState] = useState<PushState>(pushState());
  const [busy, setBusy] = useState(false);
  const on = state === 'granted' && me.device.push;

  const enable = async () => {
    setBusy(true);
    try {
      const s = await enablePush();
      setState(s);
      if (s === 'granted') {
        onMe({ ...me, device: { ...me.device, push: true } });
        toast.show({ tone: 'success', title: 'اعلان‌ها روی این دستگاه روشن شد' });
      }
    } catch (e) {
      toast.show({
        tone: 'danger',
        title:
          e instanceof Error && e.message === 'NO_PUSH_KEY'
            ? 'اعلان روی سرور هنوز راه‌اندازی نشده است.'
            : errorMessage(e),
      });
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    try {
      await api.post('/push/test');
      toast.show({ tone: 'info', title: 'اعلان آزمایشی ارسال شد؛ چند ثانیه صبر کنید.' });
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
    }
  };
  const [confirm, setConfirm] = useState(false);
  const unpair = async () => {
    try {
      await api.post('/auth/unpair');
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
      return;
    }
    await forgetDevice();
    onSignedOut();
  };

  return (
    <BottomSheet title="تنظیمات میز پشتیبانی" isOpen onOpenChange={(o) => !o && onClose()}>
      <div className="flex flex-col gap-3 pb-2" data-settings>
        <section className="rounded-[18px] border border-line bg-surface-1 p-3.5">
          <p className="m-0 flex items-center gap-2 text-[13.5px] font-extrabold text-fg">
            {on ? (
              <BellRing aria-hidden className="size-5 text-positive" />
            ) : (
              <Bell aria-hidden className="size-5 text-pending" />
            )}
            اعلان گفتگوهای تازه
          </p>
          <p className="m-0 mt-1 text-[12.5px] leading-6 text-fg-muted">
            وضعیت: {on ? 'روشن' : PUSH_FA[state]}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!on && state !== 'unsupported' && state !== 'needs-install' && state !== 'denied' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void enable()}
                className="hx-press min-h-11 cursor-pointer rounded-full bg-accent px-5 text-[13.5px] font-extrabold text-on-accent shadow-cta disabled:opacity-50"
                data-enable-push
              >
                روشن کردن اعلان
              </button>
            )}
            {on && (
              <button
                type="button"
                onClick={() => void test()}
                className="min-h-10 cursor-pointer rounded-full border border-line-strong px-4 text-[13px] font-bold text-fg"
                data-test-push
              >
                ارسال اعلان آزمایشی
              </button>
            )}
          </div>
          {state === 'denied' && (
            <p className="m-0 mt-2 text-[11.5px] leading-6 text-fg-subtle">
              در آیفون: تنظیمات ← اعلان‌ها ← پشتیبانی هوپکس ← اجازه‌ی اعلان. اگر نبود، برنامه را از
              صفحه‌ی اصلی حذف و دوباره اضافه کنید.
            </p>
          )}
        </section>

        <section className="flex items-center justify-between gap-3 rounded-[18px] border border-line bg-surface-1 p-3.5">
          <span className="text-[13px] font-bold text-fg">پوسته</span>
          <ThemeToggle />
        </section>

        <section className="rounded-[18px] border border-line bg-surface-1 p-3.5">
          <p className="m-0 flex items-center gap-2 text-[13.5px] font-extrabold text-fg">
            <Smartphone aria-hidden className="size-5 text-accent-text" />
            این دستگاه
          </p>
          <dl className="m-0 mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
            <dt className="text-fg-muted">نام</dt>
            <dd className="m-0 text-fg">{me.device.label}</dd>
            <dt className="text-fg-muted">کارشناس</dt>
            <dd className="m-0 text-fg">{me.displayName}</dd>
            <dt className="text-fg-muted">اتصال</dt>
            <dd className="m-0 text-fg">{formatJalali(me.device.pairedAt)}</dd>
            <dt className="text-fg-muted">اعتبار تا</dt>
            <dd className="m-0 text-fg">
              {formatJalali(me.device.expiresAt, { time: false })} (با هر استفاده تمدید می‌شود)
            </dd>
          </dl>
        </section>

        <div className="flex flex-col gap-2">
          {confirm ? (
            <div
              className="rounded-[18px] border border-negative bg-negative-tint p-3.5"
              role="alertdialog"
              aria-label="تأیید جدا کردن دستگاه"
            >
              <p className="m-0 text-[12.5px] leading-6 text-fg">
                این دستگاه برای همیشه از میز پشتیبانی جدا می‌شود و برای اتصال دوباره کد تازه از مدیر
                لازم است.
              </p>
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => void unpair()}
                  className="min-h-10 flex-1 cursor-pointer rounded-full bg-negative text-[13px] font-bold text-white"
                  data-unpair-confirm
                >
                  بله، جدا کن
                </button>
                <button
                  type="button"
                  onClick={() => setConfirm(false)}
                  className="min-h-10 cursor-pointer rounded-full border border-line-strong px-4 text-[13px] font-bold text-fg"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirm(true)}
              className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-line-strong text-[13px] font-bold text-negative"
              data-unpair
            >
              <LogOut aria-hidden className="size-4" /> جدا کردن این دستگاه از میز پشتیبانی
            </button>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
